// src/schemas/font.ts
import { Type } from "@sinclair/typebox";
import { FontFamilyNameSchema, TextCaseSchema } from "@json-to-office/shared";
var HexColorSchema = Type.String({
  pattern: "^(#[0-9A-Fa-f]{6}|[a-zA-Z][a-zA-Z0-9]*)$",
  description: 'Hex color with # prefix (e.g. "#000000") or theme color name'
});
var HexColorOrTransparentSchema = Type.String({
  pattern: "^(transparent|#[0-9A-Fa-f]{6}|[a-zA-Z][a-zA-Z0-9]*)$",
  description: 'Hex color with # prefix, theme color name, or "transparent"'
});
var LanguageSchema = Type.String({
  pattern: "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$",
  description: 'BCP-47 language tag for spell/grammar checking (e.g. "en-US", "fr-FR", "de-DE")',
  examples: ["en-US", "fr-FR", "de-DE", "it-IT", "es-ES"]
});
var NoProofSchema = Type.Boolean({
  description: "Disable spell/grammar checking for this text (e.g. code snippets or identifiers)"
});
var NoProofWordsSchema = Type.Array(Type.String({ minLength: 1 }), {
  description: "Words that should never be flagged as misspelled (whole-word, case-insensitive). E.g. brand names or technical terms.",
  examples: [["Wiseair", "json-to-office", "pptx"]]
});
var TextFormattingPropertiesSchema = Type.Object(
  {
    case: Type.Optional(TextCaseSchema),
    size: Type.Optional(Type.Number({ minimum: 8, maximum: 1638 })),
    color: Type.Optional(HexColorSchema),
    bold: Type.Optional(Type.Boolean()),
    fontWeight: Type.Optional(
      Type.Integer({
        minimum: 100,
        maximum: 900,
        description: "Per-run weight (100\u2013900). Any integer accepted; renderer picks the closest embedded variant via CSS font-matching. `bold: true` is shorthand for `fontWeight: 700`; if both are set, `fontWeight` wins."
      })
    ),
    italic: Type.Optional(Type.Boolean()),
    underline: Type.Optional(Type.Boolean()),
    lineSpacing: Type.Optional(
      Type.Object(
        {
          type: Type.Union(
            [
              Type.Literal("single"),
              Type.Literal("atLeast"),
              Type.Literal("exactly"),
              Type.Literal("double"),
              Type.Literal("multiple")
            ],
            {
              description: "`single`/`double` ignore `value`; `multiple` scales single spacing by it; `atLeast` is a floor the line grows past; `exactly` pins an absolute box the glyphs are clipped to."
            }
          ),
          value: Type.Optional(
            Type.Number({
              minimum: 0,
              description: "Points for `exactly` and `atLeast`, a factor of single spacing for `multiple`. Unlike `size` this has no floor \u2014 an empty spacer paragraph legitimately pins a fraction of a point \u2014 so an `exactly` box too small for the text it holds is reported as W_QUALITY_LINE_BOX_COLLAPSE instead."
            })
          )
        },
        { additionalProperties: false }
      )
    ),
    spacing: Type.Optional(
      Type.Object(
        {
          before: Type.Optional(Type.Number({ minimum: 0 })),
          after: Type.Optional(Type.Number({ minimum: 0 }))
        },
        { additionalProperties: false }
      )
    ),
    characterSpacing: Type.Optional(
      Type.Object(
        {
          type: Type.Union([
            Type.Literal("condensed"),
            Type.Literal("expanded")
          ]),
          value: Type.Number()
        },
        { additionalProperties: false }
      )
    ),
    scale: Type.Optional(
      Type.Number({
        minimum: 1,
        maximum: 600,
        description: "Character width scaling in percent (w:w). 100 is normal; values below compress glyphs, above expand them (e.g. 55, 115)."
      })
    )
  },
  { additionalProperties: false }
);
var FontDefinitionSchema = Type.Object(
  {
    family: FontFamilyNameSchema,
    ...TextFormattingPropertiesSchema.properties
  },
  {
    description: "Font definition with family and optional formatting properties",
    additionalProperties: false
  }
);

export {
  HexColorSchema,
  HexColorOrTransparentSchema,
  LanguageSchema,
  NoProofSchema,
  NoProofWordsSchema,
  TextFormattingPropertiesSchema,
  FontDefinitionSchema
};
//# sourceMappingURL=chunk-XRVPNHNS.js.map